import type { Assessment } from '@brighterly/lib-core-types/assessment/interfaces'
import type { DisciplineInfo } from '@brighterly/lib-core-types/discipline/interfaces'
import type { KidInCustomer } from '@brighterly/lib-core-types/kid/interfaces'
import type { KidStore } from '@stores/kid/interfaces'

import { useDisciplineStore } from '@stores/discipline'

import { useRawKidInCustomer } from '@hooks/kid/kid-in-customer.hook'

import { isFullArray } from '@helpers/array'

export const hasActiveKid = (state: KidStore): boolean => (typeof state.activeKidId === 'number')

const getActiveKidDisciplinesWithoutCurrent = (state: KidStore): KidStore['activeKidDisciplines'] => {
  const disciplineStore = useDisciplineStore()
  const disciplines = toRaw(state.activeKidDisciplines)

  return disciplines.filter(discipline => discipline.code !== disciplineStore.getDisciplineCode)
}

const isActiveKidDisciplinesWithoutCurrent = (state: KidStore): boolean => isFullArray(getActiveKidDisciplinesWithoutCurrent(state))

const getActiveKidReversedDisciplineInfo = (state: KidStore): DisciplineInfo | null => {
  if (!isActiveKidDisciplinesWithoutCurrent(state)) {
    return null
  } else {
    const disciplines = getActiveKidDisciplinesWithoutCurrent(state)
    return disciplines?.[0] || null
  }
}

const getActiveKidReversedDisciplineInfoDemos = (state: KidStore): DisciplineInfo['demos'] => getActiveKidReversedDisciplineInfo(state)?.demos || []
const getActiveKidReversedDisciplineInfoAssessments = (state: KidStore): DisciplineInfo['assessments'] => getActiveKidReversedDisciplineInfo(state)?.assessments || []
const isActiveKidReversedDisciplineInfoAssessments = (state: KidStore): boolean => isFullArray(getActiveKidReversedDisciplineInfoAssessments(state))

const getActiveKidReversedDisciplineLastAssessment = (state: KidStore): Assessment | null => {
  if (!isActiveKidReversedDisciplineInfoAssessments(state)) {
    return null
  } else {
    const assessments = getActiveKidReversedDisciplineInfoAssessments(state)
    return assessments?.[assessments.length - 1] || null
  }
}

export const getters = {
  hasActiveKid: (state: KidStore): boolean => hasActiveKid(state),
  getActiveKid: (state: KidStore): KidInCustomer | null => {
    return useRawKidInCustomer({ kidId: state.activeKidId }).kid
  },
  getActiveKidName: (state: KidStore): KidInCustomer['name'] => {
    return useRawKidInCustomer({ kidId: state.activeKidId }).name
  },
  getActiveKidDisciplines: (state: KidStore): KidStore['activeKidDisciplines'] => state.activeKidDisciplines,
  isActiveKidDisciplines: (state: KidStore): boolean => isFullArray(state.activeKidDisciplines),
  getActiveKidDisciplinesWithoutCurrent: (state: KidStore): KidStore['activeKidDisciplines'] => getActiveKidDisciplinesWithoutCurrent(state),
  isActiveKidDisciplinesWithoutCurrent: (state: KidStore): boolean => isActiveKidDisciplinesWithoutCurrent(state),
  getActiveKidReversedDisciplineInfo: (state: KidStore): DisciplineInfo | null => getActiveKidReversedDisciplineInfo(state),
  isActiveKidReversedDisciplineInfo: (state: KidStore): boolean => !!getActiveKidReversedDisciplineInfo(state),
  getActiveKidReversedDisciplineInfoDemos: (state: KidStore): DisciplineInfo['demos'] => getActiveKidReversedDisciplineInfoDemos(state),
  isActiveKidReversedDisciplineInfoDemos: (state: KidStore): boolean => isFullArray(getActiveKidReversedDisciplineInfoDemos(state)),
  getActiveKidReversedDisciplineInfoAssessments: (state: KidStore): DisciplineInfo['assessments'] => getActiveKidReversedDisciplineInfoAssessments(state),
  isActiveKidReversedDisciplineInfoAssessments: (state: KidStore): boolean => isActiveKidReversedDisciplineInfoAssessments(state),
  getActiveKidReversedDisciplineLastAssessment: (state: KidStore): Assessment | null => getActiveKidReversedDisciplineLastAssessment(state),
  isActiveKidReversedDisciplineLastAssessment: (state: KidStore): boolean => !!getActiveKidReversedDisciplineLastAssessment(state),
}
